import React, { useState } from "react";
import { Button, Card, CircularProgress, Grid, IconButton, Slider, Switch, TableCell, TextField, Typography } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { RootGrid, HeaderTypography } from "../../catalogmanager/containers/settings/containers/taxonomy";
import EnhancedSelect from "../../../components/EnhancedSelect";
import EnhancedTextField from "../../../components/EnhancedTextField";
import Config from "../../../config";
import moment from "moment";
import { getCompanyNames, StorageHelper } from "../../../helpers";
import CatalogCheckbox from "../../../components/EnhancedCheckbox";
import { EventIdColumn } from "../../catalogmanager/containers/catalog/components/catalog_columns";
import EnhancedTable from "../../../components/table";
import CatalogEventResults from "../../catalogmanager/containers/catalog/components/catalog_event_results";
import { utilGetPartners, utilGetPartnerSettings } from "../../../utils/utils";
import axios from "axios";
import EnhancedErrorDialog from "../../../components/EnhancedErrorDialog";
import { Properties } from "../shared/feeds";
import DownloadButton from "../../../components/download_button";
import Auth from "../../../auth";
import { Alert } from '@mui/material';
import EnhancedConfirmDialog from "../../../components/EnhancedConfirmDialog";
import {pageContentGap, CardHeader} from "../../../theme";
import type { InventoryErrorState, InventorySettings, Partner, RouteComponentPropsLike, SelectOption, SetTitle } from "./types";
export const inventorySettingsEventConfig = {
    url: Config.api + "/odata/Company/VwActivityEvents",
    savedSearchEnabled: true,
    columns: [
        {
            id: 'EventDate',
            type: 'date',
            filterable: true,
            sortable: true,
            label: 'Date',
            template: (row, column, colIndex, rowIndex) => (
                <Typography>{moment(row.EventDate).format('MM/DD/YYYY hh:mm A')}</Typography>
            ),
            width: '15em'
        },
        { id: 'PartnerCoId', label: "Partner CoId", hidden: true, filterable: true, sortable: true, width: '8em' },
        {
            id: 'PartnerCompanyName',
            filterable: true,
            sortable: true,
            label: 'Partner',
            width: '14em',
            getOptions: getCompanyNames,
            alwaysShowTooltip: true,
            template: (row) => row.PartnerCompanyName || "All Partners",
            hoverTemplate: (row) => {
                return (
                    <Typography>
                        {row.PartnerCompanyName || "This event is not associated with a specific partner."}
                    </Typography>
                )
            }
        },
        { id: 'EventLevel', label: 'Severity', filterable: true, sortable: true, width: '6em' },
        { id: 'Summary', label: 'Summary', filterable: true, allowStringOperators: true, width: '16em' },
        { id: 'Details', label: 'Details', filterable: true, allowStringOperators: true, width: '16em',
            hoverTemplate: (row, column) => row[column.id] || ''
        },
        {
            id: 'Viewed', label: "Viewed", hidden: true, sortable: true, template: (row, column, colIndex, rowIndex) => {
                return (
                    <TableCell key={`${column.id}-${rowIndex}`}
                        style={{ width: column.width, maxWidth: column.width }}>
                        <CatalogCheckbox checked={row.Viewed} disabled={true} />
                    </TableCell>
                )
            }
        },
        { id: 'EventTypeId', hidden: true },
        {
            id: 'Id',
            width: '10em',
            stopPropagation: 'true',
            filterable: false,
            template: (row, column, colIndex, rowIndex, onAddFilter, setEventId, setEventViewed, setEventDialogOpen) =>
                <EventIdColumn
                    row={row} setEventId={setEventId} setEventViewed={setEventViewed}
                    setEventDialogOpen={setEventDialogOpen} />
        },
        { id: 'CustomerViewable', hidden: true, filterable: false },
    ],
    order: 'desc',
    orderBy: 'Id',
    keyField: 'Id',
    pageSize: 25,
    pageSizes: [25, 50, 100],
    title: "Event Log",
    refresh: false,
    filterInUrl: false,
    filter: {
        filters: [
            { field: "CustomerViewable", operator: 'eq', value: true, system: true },
            { field: "Category", operator: 'eq', value: "Suspension", system: true },
        ], logic: 'and'
    },
};
const clearAfterOptions = [
    { label: "Never", value: null },
    { label: "7 Days", value: "7" },
    { label: "30 Days", value: "30" },
    { label: "60 Days", value: "60" },
];
const settingsMap = {
    vendorNumber: Properties.VendorNumber,
    forceVendorNumber: Properties.ForceVendorNumber,
    allocation: Properties.Allocation,
    autoMatch: Properties.AutoMatch,
    ignoreUnmatched: Properties.IgnoreUnmatched,
    caseSensitive: Properties.CaseSensitive,
    latestFeedOnly: Properties.LatestFeedOnly,
    minimumQuantity: Properties.MinimumQuantity,
    clearAfter: Properties.ClearAfter
};
const iconButtonSx = (theme) => ({
    backgroundColor: "white",
    border: "none",
    borderRadius: "6px",
    "& svg": { color: "rgba(0, 0, 0, 0.54)" }
});
interface InventoryManagementSettingsProps extends RouteComponentPropsLike {
    isRetailer?: boolean;
    isBillable?: boolean;
    setTitle?: SetTitle;
    signalr?: unknown;
}

export default function InventoryManagementSettings(props: InventoryManagementSettingsProps) {
    const { setTitle } = props;
    const hasInventoryManagePermission = Auth.hasPermission("inventory/manage");
    const storageHelper = new StorageHelper();
    const storedIdKey = "LBInventorySettingsPartnerId";
    const [eventsConfig, setEventsConfig] = useState<any>(inventorySettingsEventConfig);
    const [partner, setPartner] = React.useState<SelectOption | "">("");
    const [partners, setPartners] = React.useState<Partner[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<InventoryErrorState>({
        open: false,
        errorHeader: "",
        errorMessage: ""
    });
    const [settings, setSettings] = React.useState<InventorySettings>({
        vendorNumber: "",
        forceVendorNumber: false,
        allocation: 100,
        autoMatch: false,
        ignoreUnmatched: false,
        caseSensitive: false,
        latestFeedOnly: false,
        clearAfter: null,
        minimumQuantity: null
    });
    const [originalSettings, setOriginalSettings] = React.useState<InventorySettings | null>(null);
    const [resetToDefaultDialog, setResetToDefaultDialog] = React.useState(false);
    //#region Helper Functions
    const hasChanges = React.useMemo(() => {
        if (!originalSettings) return false;
        return JSON.stringify(settings) !== JSON.stringify(originalSettings);
    }, [settings, originalSettings]);
    const setConfigurationProperty = async (name, value) => {
        if (partner && partner.value !== 'all') {
            if (value === null || value === undefined || value === '') {
                value = null;
            } else {
                value = value.toString();
            }
            await axios.post(Config.api + "/odata/Company/Functions.UpdateInventorySetting", {
                PropertyName: name,
                PropertyValue: value,
                PartnerCoId: partner.value
            });
        } else {
            if (!value) {
                value = null;
            } else {
                value = value.toString();
            }
            await axios.post(Config.api + "/odata/Company/Functions.UpdateGlobalInventorySetting", {
                PropertyName: name,
                PropertyValue: value
            });
        }
    }
    const saveSettings = async () => {
        setSaving(true);
        try {
            for (const [key, property] of Object.entries(settingsMap)) {
                let currentValue = settings[key];
                const originalValue = originalSettings[key];
                if (currentValue !== originalValue) {
                    // Special Case Formatting
                    if (key === 'allocation') {
                        currentValue = (currentValue as number) / 100;
                    }
                    await setConfigurationProperty(
                        property,
                        currentValue
                    );
                }
            }
            setOriginalSettings(settings);
        } catch (error) {
            setError({
                open: true,
                errorHeader: "Error",
                errorMessage: "Failed to save settings. Please try again."
            });
        }
        setSaving(false);
    }
    const loadPartners = async () => {
        setLoading(true);
        let partners = await utilGetPartners();
        if (partners.errorMessage) return;
        // Only add "All Partners" option for retailers
        if (props.isRetailer) {
            partners.push({
                Id: "all",
                CompanyName: "All Partners"
            });
        }
        let allPartners = partners.filter(p => p.Id === "all");
        let sortedPartners = partners.filter(p => p.Id !== "all").sort((a, b) => a.CompanyName.localeCompare(b.CompanyName));
        sortedPartners.unshift(...allPartners);
        setPartners(sortedPartners);
        // Get stored partner ID or default to first partner
        let storedId = storageHelper.get(storedIdKey);
        if (!storedId || !sortedPartners.find(p => p.Id.toString() === storedId.toString())) {
            storedId = sortedPartners[0].Id;
            storageHelper.set(storedIdKey, storedId);
        }
        setPartner({
            label: sortedPartners.find(p => p.Id === storedId).CompanyName,
            value: storedId
        });
        setLoading(false);
    }
    const loadSettings = async () => {
        let loadedSettings = await utilGetPartnerSettings((partner as SelectOption).value !== 'all' ? (partner as SelectOption).value : null);
        if (loadedSettings.errorMessage) {
            setError({
                open: true,
                errorHeader: "Error",
                errorMessage: loadedSettings.errorMessage
            });
            return;
        }
        const newSettings = {
            vendorNumber: loadedSettings.vendorNumber || "",
            forceVendorNumber: loadedSettings.forceVendorNumber || false,
            allocation: loadedSettings.allocation == null ? 100 : loadedSettings.allocation,
            autoMatch: loadedSettings.autoMatch || false,
            ignoreUnmatched: loadedSettings.ignoreUnmatched || false,
            caseSensitive: loadedSettings.caseSensitive || false,
            latestFeedOnly: loadedSettings.latestFeedOnly || false,
            clearAfter: loadedSettings.clearAfter || null,
            minimumQuantity: loadedSettings.minQty !== undefined ? loadedSettings.minQty : null
        };
        setSettings(newSettings);
        setOriginalSettings(newSettings);
    }
    const resetToDefault = async () => {
        try {
            setSaving(true);
            for (const [, mappedProperty] of Object.entries(settingsMap)) {
                await setConfigurationProperty(
                    mappedProperty,
                    null
                );
            }
            // Reload the settings
            await loadSettings();
        } catch (error) {
            setError({
                open: true,
                errorHeader: "Error",
                errorMessage: "Failed to reset settings. Please try again."
            });
        } finally {
            setSaving(false);
        }
    };
    //#endregion
    //#region Lifecycle
    React.useEffect(() => {
        if (setTitle) {
            setTitle('Inventory Settings', true);
        }
        loadPartners();
        // eslint-disable-next-line
    }, []);
    React.useEffect(() => {
        if (partner) {
            setEventsConfig({
                ...eventsConfig,
                filter: {
                    filters: [
                        { field: "Category", operator: 'eq', value: 'Inventory', system: true },
                        ...((partner as SelectOption).value === 'all'
                            ? [
                                { field: "PartnerCoId", operator: 'eq', value: null, system: true },
                                ...(props.isRetailer ? [{ field: 'EventTypeId', operator: 'eq', value: 39, system: true }] : [])
                            ]
                            : [{ field: "PartnerCoId", operator: 'eq', value: (partner as SelectOption).value, system: true }]
                        )
                    ], logic: 'and'
                }
            });
            loadSettings();
        }
        // eslint-disable-next-line
    }, [partner]);
    //#endregion
    return (
        <>
            <RootGrid container
                style={{ gap: pageContentGap, padding: "24px 14px 24px 14px", position: "relative" }}>
                <Grid container justifyContent={"space-between"} direction={"row"}>
                    <Grid item container justifyContent={"flex-start"} alignItems={"center"} style={{ gap: pageContentGap, width: "100%" }}>
                        <IconButton
                            sx={iconButtonSx}
                            style={{ height: "36px", width: "36px" }}
                            onClick={(e) => {
                                e.preventDefault();
                                if (props.location.key === undefined)
                                    props.history.push("/productonboardingcenter/catalog");
                                else
                                    props.history.goBack();
                            }}
                            size="large">
                            <ArrowBack style={{ height: "18px", width: "18px" }} />
                        </IconButton>
                        <HeaderTypography>
                            Settings
                        </HeaderTypography>
                    </Grid>
                </Grid>
                <Grid item size={12}>
                    <Card>
                        <Grid container style={{ padding: "16px 16px 8px 16px" }}>
                            <CardHeader>
                                Select Partner
                            </CardHeader>
                        </Grid>
                        <Grid container style={{
                            padding: "0 16px 16px 16px",
                            display: "flex",
                            flexDirection: "column",
                            gap: pageContentGap
                        }}>
                            <EnhancedSelect
                                options={partners.map(partner => ({
                                    label: partner.CompanyName,
                                    value: partner.Id
                                }))}
                                placeholder="Select..."
                                isClearable={false}
                                onChange={(e) => {
                                    setPartner(e);
                                    storageHelper.set(storedIdKey, e.value);
                                }}
                                value={partner}
                                disabled={!hasInventoryManagePermission}
                            />
                            {!hasInventoryManagePermission && (
                                <Typography style={{ color: "rgba(0, 0, 0, 0.6)", marginTop: "8px" }}>
                                    You dont have permission to manage settings, please contact your administrator.
                                </Typography>
                            )}
                        </Grid>
                    </Card>
                </Grid>
                <Grid item size={12}>
                    <Card>
                        <Grid container style={{ padding: "16px 16px 0 16px" }}>
                            <CardHeader style={{ marginBottom: "8px" }}>
                                Settings
                            </CardHeader>
                        </Grid>
                        {(partner as SelectOption).value === 'all' ? (
                            <Alert severity="info" style={{ margin: "0 16px" }}>
                                The settings configured in this section will be applied to all partners. Settings configured on per-partner basis will override these settings.
                            </Alert>
                        ) : (
                            <Alert severity="info" style={{ margin: "0 16px" }}>
                                These settings will override settings configured in the 'All Partners' section.
                            </Alert>
                        )}
                        {loading ? (
                            <CircularProgress
                                style={{
                                    margin: "2em",
                                    width: "50px",
                                    height: "50px"
                                }}
                            />
                        ) : (
                            <>
                                {!partner ? (
                                    <Grid container style={{ padding: 16 }}>
                                        <Typography>Please select a partner to view inventory settings.</Typography>
                                    </Grid>
                                ) : (
                                    <Grid container style={{ padding: "0 16px 0 16px", display: "flex", flexDirection: "column", gap: "8px" }}>
                                        {/* Vendor Number */}
                                        <Grid container justifyContent={"space-between"}
                                            style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.12)", padding: "0.5em 0" }}>
                                            <Grid container item size={6} flex={1} style={{
                                                textAlign: "left",
                                                paddingRight: "2em",
                                                flexDirection: "column",
                                                maxWidth: "50%"
                                            }}>
                                                <Typography variant="h6">Vendor Number</Typography>
                                                <Typography variant="body1" style={{ color: "rgba(0, 0, 0, 0.6)" }}>
                                                    <b>What it is:</b> This is the retailer-assigned value that
                                                    establishes the relationship between
                                                    suppliers/vendors and retailers.
                                                </Typography>
                                                <br />
                                                <Typography variant="body1" style={{ color: "rgba(0, 0, 0, 0.6)" }}>
                                                    <b>When to use this setting:</b> Populate this value if the retailer
                                                    requires it to be sent on inventory
                                                    feeds and if the retailer is using partner-based sourcing.
                                                </Typography>
                                            </Grid>
                                            <Grid container item size={6} flex={1}>
                                                <EnhancedTextField
                                                    style={{ marginTop: "2em" }}
                                                    value={settings.vendorNumber}
                                                    onChange={(e) => {
                                                        setSettings(prev => ({
                                                            ...prev,
                                                            vendorNumber: e.target.value
                                                        }));
                                                    }}
                                                    disabled={!hasInventoryManagePermission}
                                                />
                                            </Grid>
                                        </Grid>
                                        {/* Force Vendor Number */}
                                        <Grid container justifyContent={"space-between"}
                                            style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.12)", padding: "0.5em 0" }}>
                                            <Grid container item size={6} flex={1} style={{
                                                textAlign: "left",
                                                paddingRight: "2em",
                                                flexDirection: "column"
                                            }}>
                                                <Typography variant="h6">Override Vendor Number</Typography>
                                                <Typography variant="body1" style={{ color: "rgba(0, 0, 0, 0.6)" }}>
                                                    <b>What it is:</b> This will block the vendor number provided by supplier
                                                    and apply the value assigned by the retailer, shown in the section above.
                                                </Typography>
                                                <br />
                                                <Typography variant="body1" style={{ color: "rgba(0, 0, 0, 0.6)" }}>
                                                    <b>When to use this setting:</b> When the retailer requires the vendor number on
                                                    inventory and the retailer is using partner-based sourcing.
                                                </Typography>
                                            </Grid>
                                            <Grid container item size={6} style={{ marginTop: "2em" }} flex={1}>
                                                <Switch
                                                    checked={settings.forceVendorNumber}
                                                    onChange={(e) => {
                                                        setSettings(prev => ({
                                                            ...prev,
                                                            forceVendorNumber: e.target.checked
                                                        }));
                                                    }}
                                                    disabled={!hasInventoryManagePermission}
                                                />
                                            </Grid>
                                        </Grid>
                                        {/* Clear SKUs */}
                                        <Grid container justifyContent={"space-between"} flex={1}
                                            style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.12)", padding: "0.5em 0" }}>
                                            <Grid container item size={6} flex={1} style={{
                                                textAlign: "left",
                                                paddingRight: "2em",
                                                flexDirection: "column"
                                            }}>
                                                <Typography variant="h6">Clear SKUs from Feed After</Typography>
                                                <Typography variant="body1" style={{ color: "rgba(0, 0, 0, 0.6)" }}>
                                                    <b>What it is:</b> This will clear SKU quantities as well as
                                                    warehouse quantities that have not been updated after the selected
                                                    time.
                                                </Typography>
                                                <br />
                                                <Typography variant="body1" style={{ color: "rgba(0, 0, 0, 0.6)" }}>
                                                    <b>When to use this setting:</b> Configure this when feeds become
                                                    stale after a certain period of time to avoid stale inventory being
                                                    sent to the retailer.
                                                </Typography>
                                            </Grid>
                                            <Grid container item size={6} flex={1}>
                                                <EnhancedSelect
                                                    containerStyle={{ marginTop: "2em" }}
                                                    placeholder="Select..."
                                                    options={clearAfterOptions}
                                                    isClearable
                                                    onChange={(e) => {
                                                        setSettings(prev => ({
                                                            ...prev,
                                                            clearAfter: e ? e.value : null
                                                        }));
                                                    }}
                                                    value={clearAfterOptions.find(option => option.value === settings.clearAfter)}
                                                    disabled={!hasInventoryManagePermission}
                                                />
                                            </Grid>
                                        </Grid>
                                        {/* Maximum Quantity */}
                                        <Grid container justifyContent={"space-between"}
                                            style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.12)", padding: "0.5em 0" }}>
                                            <Grid container item size={6} flex={1} style={{
                                                textAlign: "left",
                                                paddingRight: "2em",
                                                flexDirection: "column"
                                            }}>
                                                <Typography variant="h6">Minimum Quantity</Typography>
                                                <Typography variant="body1" style={{ color: "rgba(0, 0, 0, 0.6)" }}>
                                                    <b>What it is:</b> This will automatically set the quantity of a SKU
                                                    to 0 if it reaches the minimum value or below. This change will be
                                                    reflected on the next feed sent to the retailer.
                                                </Typography>
                                                <br />
                                                <Typography variant="body1" style={{ color: "rgba(0, 0, 0, 0.6)" }}>
                                                    <b>When to use this setting:</b> If you would like to keep a
                                                    threshold and reduce out of stock instances.
                                                </Typography>
                                            </Grid>
                                            <Grid container item size={6} flex={1}>
                                                <EnhancedTextField
                                                    style={{ marginTop: "2em" }}
                                                    type="number"
                                                    value={settings.minimumQuantity === null ? "" : settings.minimumQuantity}
                                                    inputProps={{
                                                        min: 0,
                                                        step: 1
                                                    }}
                                                    disabled={!hasInventoryManagePermission}
                                                    onChange={(e) => {
                                                        // If the field is empty (backspace/delete), set to null
                                                        if (e.target.value === "") {
                                                            setSettings(prev => ({
                                                                ...prev,
                                                                minimumQuantity: null
                                                            }));
                                                            return;
                                                        }
                                                        // Otherwise parse the number value
                                                        const value = parseInt(e.target.value, 10);
                                                        setSettings(prev => ({
                                                            ...prev,
                                                            minimumQuantity: value
                                                        }));
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                        {/* Allocation */}
                                        <Grid container justifyContent={"space-between"}
                                            style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.12)", padding: "0.5em 0" }}>
                                            <Grid container item size={6} flex={1} style={{
                                                textAlign: "left",
                                                paddingRight: "2em",
                                                flexDirection: "column"
                                            }}>
                                                <Typography variant="h6">Allocation</Typography>
                                                <Typography variant="body1" style={{ color: "rgba(0, 0, 0, 0.6)" }}>
                                                    <b>What it is:</b> This will control the percentage of inventory
                                                    quantity allocated to a specific retailer.
                                                </Typography>
                                                <br />
                                                <Typography variant="body1" style={{ color: "rgba(0, 0, 0, 0.6)" }}>
                                                    <b>When to use this setting:</b> Use this setting if a master file
                                                    is used to share inventory with the retailer rather than a
                                                    partner-specific feed.
                                                </Typography>
                                            </Grid>
                                            <Grid container item size={6} flex={1}>
                                                <div style={{ marginTop: "2em", width: "100%", padding: "0 16px", display: "flex", flexDirection: "column", gap: pageContentGap }}>
                                                    <Slider
                                                        style={{ marginLeft: "8px", marginBottom: "10px" }}
                                                        value={settings.allocation}
                                                        onChange={(event, newValue) => {
                                                            setSettings(prev => ({
                                                                ...prev,
                                                                allocation: Number(newValue)
                                                            }));
                                                        }}
                                                        min={0}
                                                        max={100}
                                                        step={1}
                                                        marks={[
                                                            { value: 0, label: '0%' },
                                                            { value: 100, label: '100%' }
                                                        ]}
                                                        valueLabelDisplay="auto"
                                                        valueLabelFormat={(value) => `${value}%`}
                                                        disabled={!hasInventoryManagePermission}
                                                    />
                                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                        <TextField
                                                            type="number"
                                                            value={settings.allocation}
                                                            onChange={(e) => setSettings(prev => ({
                                                                ...prev,
                                                                allocation: Number(e.target.value || 0)
                                                            }))}
                                                            disabled={!hasInventoryManagePermission}
                                                            slotProps={{
                                                                htmlInput: {
                                                                    min: 0,
                                                                    max: 100,
                                                                    style: { width: "80px" }
                                                                }
                                                            }}
                                                        />
                                                        <Typography>%</Typography>
                                                    </div>
                                                </div>
                                            </Grid>
                                        </Grid>
                                        {/* Auto Match */}
                                        <Grid container justifyContent={"space-between"}
                                            style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.12)", padding: "0.5em 0" }}>
                                            <Grid container item size={6} flex={1} style={{
                                                textAlign: "left",
                                                paddingRight: "2em",
                                                flexDirection: "column"
                                            }}>
                                                <Typography variant="h6">Auto Match SKUs</Typography>
                                                <Typography variant="body1" style={{ color: "rgba(0, 0, 0, 0.6)" }}>
                                                    <b>What it is:</b> This will automatically set the Retailer SKU as
                                                    the Supplier SKU. Matching SKUs is how Logicbroker controls which
                                                    items are shared with the retailer in case a supplier can only
                                                    upload a master feed and not a partner-specific feed.
                                                </Typography>
                                                <br />
                                                <Typography variant="body1" style={{ color: "rgba(0, 0, 0, 0.6)" }}>
                                                    <b>When to use this setting:</b> When the Supplier and Retailer SKU
                                                    are the same and all items on the feed should be sent to the
                                                    retailer.
                                                </Typography>
                                            </Grid>
                                            <Grid container item size={6} style={{ marginTop: "2em" }} flex={1}>
                                                <Switch
                                                    checked={settings.autoMatch}
                                                    onChange={(e) => {
                                                        setSettings(prev => ({
                                                            ...prev,
                                                            autoMatch: e.target.checked
                                                        }));
                                                    }}
                                                    disabled={!hasInventoryManagePermission}
                                                />
                                            </Grid>
                                        </Grid>
                                        {/* Ignore Unmatched SKUs */}
                                        <Grid container justifyContent={"space-between"}
                                            style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.12)", padding: "0.5em 0" }}>
                                            <Grid container item size={6} flex={1} style={{
                                                textAlign: "left",
                                                paddingRight: "2em",
                                                flexDirection: "column"
                                            }}>
                                                <Typography variant="h6">Ignore Un-matched SKUs</Typography>
                                                <Typography variant="body1" style={{ color: "rgba(0, 0, 0, 0.6)" }}>
                                                    <b>What it is:</b> This will block updates to unmatched items when
                                                    inventory is imported into Logicbroker.
                                                </Typography>
                                                <br />
                                                <Typography variant="body1" style={{ color: "rgba(0, 0, 0, 0.6)" }}>
                                                    <b>When to use this setting:</b> When you are importing a master
                                                    file including items that are not meant for the retailer.
                                                </Typography>
                                            </Grid>
                                            <Grid container item size={6} style={{ marginTop: "2em" }} flex={1}>
                                                <Switch
                                                    checked={settings.ignoreUnmatched}
                                                    onChange={(e) => {
                                                        setSettings(prev => ({
                                                            ...prev,
                                                            ignoreUnmatched: e.target.checked
                                                        }));
                                                    }}
                                                    disabled={!hasInventoryManagePermission}
                                                />
                                            </Grid>
                                        </Grid>
                                        {/* Enable Case sensitive */}
                                        <Grid container justifyContent={"space-between"}
                                            style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.12)", padding: "0.5em 0" }}>
                                            <Grid container item size={6} flex={1} style={{
                                                textAlign: "left",
                                                paddingRight: "2em",
                                                flexDirection: "column"
                                            }}>
                                                <Typography variant="h6">Enable Case-sensitvity</Typography>
                                                <Typography variant="body1" style={{ color: "rgba(0, 0, 0, 0.6)" }}>
                                                    <b>What it is:</b> Logicbroker will auto-capitalize all SKUs.
                                                    Enabling this will keep the case of the import.
                                                </Typography>
                                                <br />
                                                <Typography variant="body1" style={{ color: "rgba(0, 0, 0, 0.6)" }}>
                                                    <b>When to use this setting:</b> When SKUs are case-sensitive or
                                                    your system is case-sensitive.
                                                </Typography>
                                            </Grid>
                                            <Grid container item size={6} style={{ marginTop: "2em" }} flex={1}>
                                                <Switch
                                                    checked={settings.caseSensitive}
                                                    onChange={(e) => {
                                                        setSettings(prev => ({
                                                            ...prev,
                                                            caseSensitive: e.target.checked
                                                        }));
                                                    }}
                                                    disabled={!hasInventoryManagePermission}
                                                />
                                            </Grid>
                                        </Grid>
                                        {/* Send latest Feed only to retailer */}
                                        <Grid container justifyContent={"space-between"}
                                            style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.12)", padding: "0.5em 0" }}>
                                            <Grid container item size={6} flex={1} style={{
                                                textAlign: "left",
                                                paddingRight: "2em",
                                                flexDirection: "column"
                                            }}>
                                                <Typography variant="h6">Send Latest Feed Only to the
                                                    Retailer</Typography>
                                                <Typography variant="body1" style={{ color: "rgba(0, 0, 0, 0.6)" }}>
                                                    <b>What it is:</b> This will ignore all feed imports and will only
                                                    transmit the most recent feed to the retailer.
                                                </Typography>
                                                <br />
                                                <Typography variant="body1" style={{ color: "rgba(0, 0, 0, 0.6)" }}>
                                                    <b>When to use this setting:</b> If multiple files were imported
                                                    incorrectly or with bad data prior to the final feed being imported.
                                                </Typography>
                                            </Grid>
                                            <Grid container item size={6} style={{ marginTop: "2em" }} flex={1}>
                                                <Switch
                                                    checked={settings.latestFeedOnly}
                                                    onChange={(e) => {
                                                        setSettings(prev => ({
                                                            ...prev,
                                                            latestFeedOnly: e.target.checked
                                                        }));
                                                    }}
                                                    disabled={!hasInventoryManagePermission}
                                                />
                                            </Grid>
                                        </Grid>
                                        {/* Save Button */}
                                        {hasInventoryManagePermission && (
                                            <Grid container style={{ padding: "0.5em 0", justifyContent: "flex-end" }}>
                                                <Button
                                                    variant="outlined"
                                                    onClick={() => setResetToDefaultDialog(true)}
                                                    disabled={saving}
                                                    style={{ marginRight: "1em" }}
                                                >
                                                    Reset to Default
                                                </Button>
                                                <DownloadButton
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={saveSettings}
                                                    disabled={!hasChanges || saving}
                                                >
                                                    {saving ? "Saving..." : "Save Changes"}
                                                </DownloadButton>
                                            </Grid>
                                        )}
                                    </Grid>
                                )}
                            </>
                        )}
                    </Card>
                </Grid>
                <Grid style={{ width: "100%" }}>
                    <EnhancedTable config={eventsConfig} history={props.history} location={props.location}
                        template={(table) =>
                            <CatalogEventResults history={props.history} location={props.location}
                                table={table} config={eventsConfig}
                                condensed={true}
                                disableOnViewAction={true}
                                setConfig={setEventsConfig}
                                inLineFilters={true}
                                tableHeader={
                                    <Grid container style={{ padding: "16px 16px 0 16px" }}>
                                        <CardHeader>Events</CardHeader>
                                    </Grid>
                                }
                                noToolBar={true}
                            />
                        } />
                </Grid>
            </RootGrid>
            <EnhancedConfirmDialog
                open={resetToDefaultDialog}
                setOpen={setResetToDefaultDialog}
                action={(action) => {
                    if (action) resetToDefault();
                }}
                type="delete"
                customConfirmText="Reset"
                message={<Typography>Are you sure you want to reset inventory settings to the global defaults?</Typography>}
            />
            <EnhancedErrorDialog
                open={error.open}
                onClose={() => setError({ ...error, open: false })}
                errorHeader={error.errorHeader}
                errorMessage={error.errorMessage}
            />
        </>
    );
}
